// Sprint T-22 · NotificationItem · single item con acciones
import React, { useState } from "react";
import {
  ArrowUUpLeft,
  Check,
  DotsThreeVertical,
  Trash,
} from "@phosphor-icons/react";
import { getKindMeta } from "../../lib/notificationKindsMap";

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const s = Math.floor(diff / 1000);
    if (s < 60) return "ahora";
    const m = Math.floor(s / 60);
    if (m < 60) return `hace ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `hace ${d} d`;
    return new Date(iso).toLocaleDateString("es-ES");
  } catch {
    return "";
  }
}

/**
 * NotificationItem (T-22)
 * · Icono según kind + estado unread (punto ámbar) + tiempo relativo.
 * · Deep-link click → onClick(n).
 * · Menú contextual con: marcar leído / no leído, descartar.
 *
 * Props:
 *   · n         → objeto notificación (serialize del backend)
 *   · onOpen    → (n) => void · handler cuando el usuario clica el body/deep_link
 *   · onMarkRead   → (id) => Promise<void>
 *   · onMarkUnread → (id) => Promise<void>
 *   · onDismiss    → (id) => Promise<void>
 */
export const NotificationItem = ({
  n,
  onOpen,
  onMarkRead,
  onMarkUnread,
  onDismiss,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const meta = getKindMeta(n.notification_kind);
  const Icon = meta.icon;
  const isUnread = !n.read;

  const handleBodyClick = (e) => {
    // Evita conflicto si el clic viene del menú
    if (e.target.closest("[data-notif-menu]")) return;
    onOpen?.(n);
  };

  const menuAction = async (fn) => {
    setMenuOpen(false);
    try {
      await fn?.(n.id);
    } catch {
      /* silencioso · toast lo maneja el padre */
    }
  };

  return (
    <li
      className={[
        "relative px-4 py-3 transition-colors border-b border-[var(--ag-border)]/50 last:border-b-0",
        isUnread ? "bg-[var(--ag-midnight)]/60" : "bg-transparent",
        n.deep_link ? "hover:bg-[var(--ag-midnight)]/80 cursor-pointer" : "hover:bg-[var(--ag-midnight)]/40",
      ].join(" ")}
      onClick={handleBodyClick}
      data-testid={`notif-item-${n.id}`}
      data-notif-unread={isUnread ? "true" : "false"}
    >
      <div className="flex items-start gap-3">
        {/* Icono kind */}
        <div className="mt-0.5 shrink-0 w-8 h-8 rounded-md bg-[var(--ag-midnight)]/70 border border-[var(--ag-border)]/60 flex items-center justify-center text-[var(--ag-amber,#f59e0b)]">
          <Icon size={16} weight={isUnread ? "fill" : "regular"} />
        </div>

        {/* Cuerpo */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isUnread && (
              <span
                className="shrink-0 w-2 h-2 rounded-full bg-[var(--ag-amber,#f59e0b)]"
                data-testid={`notif-item-dot-${n.id}`}
                aria-label="No leída"
              />
            )}
            <div
              className={[
                "text-sm truncate",
                isUnread ? "text-white font-semibold" : "text-[var(--ag-text)] font-medium",
              ].join(" ")}
              data-testid={`notif-item-title-${n.id}`}
            >
              {n.title || "Notificación"}
            </div>
          </div>
          {n.body && (
            <div
              className="text-xs text-[var(--ag-text-dim)] mt-0.5 line-clamp-2"
              data-testid={`notif-item-body-${n.id}`}
            >
              {n.body}
            </div>
          )}
          <div className="text-[11px] text-[var(--ag-text-dim)]/80 mt-1.5 flex items-center gap-2">
            <span data-testid={`notif-item-time-${n.id}`}>
              {formatRelative(n.created_at)}
            </span>
            <span aria-hidden="true">·</span>
            <span
              className="opacity-80"
              data-testid={`notif-item-kind-${n.id}`}
            >
              {meta.label}
            </span>
          </div>
        </div>

        {/* Menú acciones */}
        <div className="relative shrink-0" data-notif-menu>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="p-1.5 rounded text-[var(--ag-text-dim)] hover:text-white hover:bg-[var(--ag-midnight)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ag-amber,#f59e0b)]/50"
            aria-label="Más acciones"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid={`notif-item-menu-toggle-${n.id}`}
          >
            <DotsThreeVertical size={16} weight="bold" />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 min-w-[190px] rounded-md border border-[var(--ag-border)] bg-[var(--ag-midnight)] shadow-xl z-10 py-1"
              role="menu"
              data-testid={`notif-item-menu-${n.id}`}
            >
              {isUnread ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    menuAction(onMarkRead);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-white hover:bg-[var(--ag-midnight)]/60"
                  role="menuitem"
                  data-testid={`notif-item-action-read-${n.id}`}
                >
                  <Check size={12} /> Marcar como leída
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    menuAction(onMarkUnread);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-white hover:bg-[var(--ag-midnight)]/60"
                  role="menuitem"
                  data-testid={`notif-item-action-unread-${n.id}`}
                >
                  <ArrowUUpLeft size={12} /> Marcar como no leída
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  menuAction(onDismiss);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-red-300 hover:bg-red-500/10"
                role="menuitem"
                data-testid={`notif-item-action-dismiss-${n.id}`}
              >
                <Trash size={12} /> Descartar
              </button>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

export default NotificationItem;
